/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'./QueryPostProcessor',
	'underscore'
], function(QueryPostProcessor, _) {

	/**
	 * This module applies a user's sort overrides to the data items. For example,
	 * A column can have the members A, B, C, D, E and F in ascending order. If a
	 * user overrides the default ascending sort order by forcing C and D to the top,
	 * this module will post process the query result for the order to be applied.
	 * At the end of the post process, the order will be C, D, A, B, E and F.
	 *
	 **/
	return class PostProcessCustomSort extends QueryPostProcessor {

		/**
		 * @Constructor
		 * @param {Object} options
		 */
		constructor(options) {
			super(options);
			this.visualization = options.visualization;
			this.visDataItems = this.visualization.getSlots().getDataItemList() || [];
			this.data = this._queryResultData.data;
			// compatible with v1 dataItems and v2 edges
			this.dataItems = this._queryResultData.edges || this._queryResultData.dataItems;
		}

		/**
		 * Apply the users overrides
		 * @return {QueryResultData}
		 */
		_processData() {
			this.dataItemsMapping = {};
			this.visDataItems.forEach((visDataItem) => {
				this.dataItemsMapping[visDataItem.getId()] = visDataItem;
			});

			let doesSortExist = false;
			this.dataItems.forEach((dataItem, dataItemIndex) => {
				if (dataItem.items && dataItem.items.length) {
					// given the data item, see if there are any custom sorts/overrides
					let topOverrides =  this._getOverrides(dataItem);

					//At least one custom sort exists within this dataItem itemClass
					if (this._findIndex(topOverrides, (topOverride) => topOverride.length) !== -1) {
						doesSortExist = true;
						//Update data items with the new sort, given the overrides
						dataItem.items = this._updateDataItemWithOverrides(dataItem, dataItemIndex, topOverrides);
					}
				}
			});

			// Sort data by indices if there exists categorical data.
			// Sort priority is determined by dataItem index
			if (doesSortExist === true) {
				this._queryResultData.data.sort((a,b) => {
					// To handle the clipper case where one of the data point is { hasNext: true } which indicates more points available
					if(!a.pt || !b.pt){
						return 0;
					}
					for (let i = 0; i < this.visDataItems.length; i++) {
						const aPt = a.pt[i];
						const bPt = b.pt[i];
						if (!isNaN(aPt) && !isNaN(bPt) && aPt !== bPt) {
							return aPt < bPt ? -1 : 1;
						} else if (!isNaN(aPt.v) && !isNaN(bPt.v) && aPt.v !== bPt.v) {
							const sort = this.visDataItems[i].getSort();
							if (sort && sort.type=== 'desc') {
								return aPt.v < bPt.v ? 1 : -1;
							} else if (sort && sort.type=== 'asc') {
								return aPt.v < bPt.v ? -1 : 1;
							}
						}
					}
					return 0;
				});
			}

			//Process Done
			return this._queryResultData;
		}

		_getItemPriority(itemU, topOverrides) {
			let weight = 0;
			for (let i = 0; i < topOverrides.length; i++) {
				const topOverride = topOverrides[i];
				const index = topOverride.indexOf(itemU);
				if (index !== -1) {
					weight += topOverrides.length - i + topOverride.length - index;
				}
			}
			return weight;
		}

		/**
		 * @desc This function recursively adds the descendants of expanded members and their expanded descendants to the overrides list.
		 * Children of any member are identified by their pu (parentUniqueName) attribute.
		 */
		_getDescendantOverrides(dataItem, u) {
			const result = [];
			dataItem.identifiers && Object.values(dataItem.identifiers).forEach(identifier => {
				if (identifier.pu === u) {
					result.push(identifier.u);
					const descendants = this._getDescendantOverrides(dataItem, identifier.u);
					if (descendants.length) {
						result.push(...descendants);
					}
				}
			});
			return result;
		}

		_getOverrides(dataItem) {
			// compatible with v1 itemClass and v2 itemClasses
			const itemClass = dataItem.itemClass || dataItem.itemClasses[0];
			return itemClass.h.map((itemClass, index) => {
				// There is a possibility when the item is not found. This happens when the item is added to the query request and is not part of the visualization data.
				//For example, local filters are added ot the query request but is not part of the visualization data. In this case, the item will be undefined, and we simply ignore it
				let item = this._getMatchingItem(dataItem,index);
				let sortObj = (item) ? item.getSort() : undefined;
				let isSortObjExist = (sortObj && sortObj.custom && sortObj.custom.enabled && sortObj.custom.top && sortObj.custom.top.length) ? true : false;
				let override = [];
				if (item && isSortObjExist) {
					override = sortObj.custom.top;
				}
				//dataItem.identifiers is introduced in v2
				if (dataItem.identifiers) {
					const overrideWithDescendants = [];
					override.forEach(u => {
						overrideWithDescendants.push(u);
						const descendants = this._getDescendantOverrides(dataItem, u);
						if (descendants && descendants.length) {
							overrideWithDescendants.push(...descendants);
						}
					});
					return overrideWithDescendants;
				}
				return override;
			});
		}

		/**
		 * If slot contains one data-item, use the data-item id to fetch the item
		 * If slot contains more than one data-item, use the id (ex: categories) to fetch all
		 * 		data-items that exist in the slot. Then loop and get items one at a time,
		 * 		given the index for the dataItem you are on
		 * @return data item, given the Id. If no match (ex: local filters), return undefined
		 */
		_getMatchingItem(dataItem, index) {
			let item;
			if (dataItem.itemClass) {
				if (dataItem.itemClass.h.length > 1) {
					const dataItems = this.visualization.getSlots().getSlot(dataItem.itemClass.id).getDataItemList();
					item = this.dataItemsMapping[dataItems[index].getId()];
				} else {
					item = this.dataItemsMapping[dataItem.itemClass.id];
				}
			} else {
				// compatible with v1 itemClass and v2 itemClasses
				item = this.dataItemsMapping[dataItem.itemClasses[0].h[index].di];
			}
			return item;
		}

		_updateDataItemWithOverrides(dataItem, dataItemIndex, topOverrides) {
			//Update the data item items' ref in the data points array
			const refMap = {};
			const identifiers = dataItem.identifiers;
			const sortedItems = _.chain(dataItem.items)
				.filter(item => Array.isArray(item.t))
				.map((item, index) => {
					item.originalIndex = index;
					return item;
				})
				.value()
				.sort((itemA, itemB) => {
					let order = 0;
					// loop through the item array, if there's more than one then find the first item that's different, then...
					// order according to their top overrides and default to the natural order determined by their indices in the incoming list
					for (let i = 0; i < itemA.t.length; i++) {
						// compatible with v1 dataitem.items and v2 edge.items
						const itemAUId = itemA.t[i].u || identifiers[itemA.t[i].rId].u;
						const itemBUId = itemB.t[i].u || identifiers[itemB.t[i].rId].u;
						if (itemAUId !== itemBUId) {
							const itemAWeight = this._getItemPriority(itemAUId, topOverrides);
							const itemBWeight = this._getItemPriority(itemBUId, topOverrides);
							if (itemAWeight !== itemBWeight) {
								order = itemBWeight - itemAWeight;
							}
							break;
						}
					}
					return order || itemA.originalIndex - itemB.originalIndex;
				});

			sortedItems.forEach((item, index) => {
				refMap[item.originalIndex] = index;
				delete item.originalIndex;
			});
			this.data.forEach((dataPoint) => {
				// To handle the clipper case where one of the data point is { hasNext: true } which indicates more points available
				if(dataPoint.pt){
					dataPoint.pt[dataItemIndex] = refMap[dataPoint.pt[dataItemIndex]];
				}
			});

			//return with new sort list of data
			return sortedItems;
		}

		_findIndex(array, predicate) {
			const len = array.length;
			let k = 0;
			while (k < len) {
				let kValue = array[k];
				if (predicate.call(this, kValue, k, array)) {
					return k;
				}
				k++;
			}
			return -1;
		}
	};

});
